import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { Avatar, GenderBadge, computeDefaults, computeConnectors, NODE_W, NODE_H, AVATAR, PAD_X, PAD_Y } from "@/modules/FamilyTree";
import { TreeDeciduous, Loader2, ArrowRight } from "lucide-react";

export default function PublicFamilyTree({ token, onHome }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/family-tree/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This shared tree is not available."));
  }, [token]);

  const nodes = data?.nodes || [];
  const meta = useMemo(() => (nodes.length ? computeDefaults(nodes) : null), [nodes]);
  const positions = meta?.pos || {};
  const dims = useMemo(() => {
    let w = meta?.defaultW || 600, h = meta?.defaultH || 400;
    Object.values(positions).forEach((p) => { w = Math.max(w, p.x + NODE_W + PAD_X); h = Math.max(h, p.y + NODE_H + PAD_Y); });
    return { w, h };
  }, [positions, meta]);
  const { childPaths, spousePaths } = useMemo(() => (nodes.length ? computeConnectors(nodes, positions) : { childPaths: [], spousePaths: [] }), [nodes, positions]);

  if (error) {
    return (
      <div className="hp-parchment-texture min-h-screen grid place-items-center px-5">
        <div className="max-w-md text-center rounded-3xl bg-white border border-[#E4DCD3] p-9 shadow-sm">
          <TreeDeciduous size={34} className="text-[#7BB661] mx-auto mb-3" />
          <h1 className="font-serif text-2xl font-bold text-[#180D24]">Tree unavailable</h1>
          <p className="mt-2 text-sm text-[#756D7E]">{error}</p>
          <button onClick={onHome} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 font-bold hover:bg-[#482276] transition-colors">
            Visit WhatsMyTribe.com <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="hp-parchment-texture min-h-screen grid place-items-center"><Loader2 size={26} className="animate-spin text-[#59288E]" /></div>;

  return (
    <div className="hp-parchment-texture min-h-screen" data-testid="public-family-tree">
      <header className="bg-[#2A1442] text-white px-5 sm:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="grid place-items-center h-10 w-10 rounded-xl border border-[#D4AF37]/60 bg-[#180D24]">
            <img src="/logo.png" alt="WhatsMyTribe" className="h-7 w-7 object-contain" />
          </div>
          <div>
            <div className="font-serif text-lg font-bold leading-tight">{data.owner}'s Family Tree</div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#E7C965]">Shared via WhatsMyTribe.com</div>
          </div>
        </div>
        <button data-testid="public-cta-btn" onClick={onHome} className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-5 py-2.5 text-sm font-bold hover:bg-[#E7C965] transition-colors">
          Start your own tree <ArrowRight size={15} />
        </button>
      </header>

      <div className="p-4 sm:p-8">
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-3 rounded-2xl bg-[#2A1442] px-6 py-3">
            <TreeDeciduous size={22} className="text-[#7BB661]" />
            <div className="font-serif text-lg font-bold text-white tracking-wide">FAMILY TREE</div>
          </div>
        </div>

        <div className="overflow-auto rounded-3xl border border-[#E4DCD3] bg-[#FBF7EF]">
          <div className="relative" style={{ width: dims.w, height: dims.h, minWidth: "100%" }} data-testid="public-tree-canvas">
            <svg className="absolute inset-0 pointer-events-none" width={dims.w} height={dims.h} style={{ zIndex: 0 }}>
              {childPaths.map((p) => <path key={p.key} d={p.d} fill="none" stroke="#8F7220" strokeWidth="2" strokeDasharray="5 5" opacity="0.7" />)}
              {spousePaths.map((s) => <line key={s.key} x1={s.x1} y1={s.y} x2={s.x2} y2={s.y} stroke="#D4AF37" strokeWidth="3" />)}
            </svg>
            {meta?.gens.map((g) => (
              <div key={g} className="absolute text-[10px] uppercase font-mono tracking-[0.2em] text-[#C3B393]" style={{ left: 10, top: (meta.rowY[g] || 0) + 34 }}>Gen {g}</div>
            ))}
            {nodes.map((n) => {
              const p = positions[n.id] || { x: 0, y: 0 };
              return (
                <div key={n.id} style={{ position: "absolute", left: p.x, top: p.y, width: NODE_W, zIndex: 1 }} className="flex flex-col items-center text-center">
                  <Avatar node={n} />
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#180D24] px-3 py-1 text-[10px] font-bold uppercase tracking-wide max-w-full">
                    <GenderBadge gender={n.gender} />
                    <span className="truncate">{n.relation || "Relative"}</span>
                  </div>
                  <div className="mt-1.5 font-serif text-sm font-semibold text-[#180D24] leading-tight">{n.name}</div>
                  <div className="text-[11px] text-[#8F7220] font-mono">{n.birth || "?"}{n.death ? `–${n.death}` : ""}</div>
                  <div className="mt-1"><EvidenceBadge status={n.evidence_status} size="sm" /></div>
                </div>
              );
            })}
            <div className="absolute text-[10px] font-mono text-[#B49A6A]" style={{ left: 16, top: dims.h - 22 }}>WhatsMyTribe.com — Powered by ISelfLawAmMaster.com</div>
          </div>
        </div>
      </div>
    </div>
  );
}
